import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Search, Loader2, ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

const categorySchema = z.object({
  category: z.string().min(1, 'Category is required'),
  subCategory: z.string().min(1, 'Sub Category is required'),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

interface Category {
  id: string;
  category: string;
  subCategory: string;
  businessCount: string | number;
}

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [search, setSearch] = useState('');

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      category: '',
      subCategory: '',
    },
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/admin/categories');
      setCategories(response.data.categories);
    } catch (_error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (editingCategory) {
      form.reset({
        category: editingCategory.category,
        subCategory: editingCategory.subCategory,
      });
    } else {
      form.reset({
        category: '',
        subCategory: '',
      });
    }
  }, [editingCategory, form]);

  const onSubmit = async (values: CategoryFormValues) => {
    try {
      if (editingCategory) {
        await axiosInstance.patch(`/api/admin/categories/${editingCategory.id}`, values);
        toast.success('Category updated successfully');
      } else {
        await axiosInstance.post('/api/admin/categories', values);
        toast.success('Category created successfully');
      }
      setDialogOpen(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (_error) {
      toast.error('Failed to save category');
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      await axiosInstance.delete(`/api/admin/categories/${id}`);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (_error) {
      toast.error('Failed to delete category');
    }
  };

  const filteredCategories = categories.filter(
    (c) =>
      c.category.toLowerCase().includes(search.toLowerCase()) ||
      c.subCategory.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 space-y-6 max-w-6xl mx-auto">
      <Link
        to="/dashboard"
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Link>
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-premium bg-clip-text text-transparent">
            Business Categories
          </h1>
          <p className="text-muted-foreground">
            Manage the master list of business categories and subcategories.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingCategory(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Category
        </Button>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                className="pl-10 h-11 bg-background/50 border-input focus:border-primary/50"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="text-sm font-medium text-muted-foreground px-4 py-2 rounded-full bg-accent/10 border border-border/5">
              Total: {categories.length} / 200
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Sub Category</TableHead>
                <TableHead className="text-center">Businesses</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary opacity-50" />
                  </TableCell>
                </TableRow>
              ) : filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                    No categories found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((category) => (
                  <TableRow key={category.id} className="group transition-colors hover:bg-accent/5">
                    <TableCell className="font-semibold text-foreground">
                      {category.category}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{category.subCategory}</TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="secondary"
                        className="bg-primary/10 text-primary border-primary/20"
                      >
                        {category.businessCount}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1 opacity-50 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="hover:text-primary hover:bg-primary/5"
                        onClick={() => {
                          setEditingCategory(category);
                          setDialogOpen(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        onClick={() => onDelete(category.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">Main Category</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Technology"
                        className="h-12 bg-background/50 border-input focus:border-primary/50"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="subCategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">Sub Category</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Software Development"
                        className="h-12 bg-background/50 border-input focus:border-primary/50"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="pt-4">
                <Button
                  type="submit"
                  className="w-full sm:w-auto px-8 h-12 bg-gradient-premium shadow-lg"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
